<template>
  <div class="user-email-alias px-4">
    <h2 class="font-weight-bold">
      {{ $t('profile.views.EmailAlias.title') }}
    </h2>

    <h3 class="mt-6 text--large text--bold">
      {{ $t("profile.views.EmailAlias.emailAddress") }}
    </h3>
    <p class="text--small text--regular">
      {{ $t("profile.views.EmailAlias.yourEmailAddress") }}
      <span class="text--bold">
        {{ profile.email }}
      </span>
    </p>

    <h3 class="mt-6 text--large text--bold">
      {{ $t("profile.views.EmailAlias.alias") }}
    </h3>
    <p class="mb-6 text--small text--regular">
      {{ $t("profile.views.EmailAlias.aliasDescription") }}
    </p>

    <p class="text--small text--bold">
      {{ $t("profile.views.EmailAlias.addAlias") }}
    </p>
    <div class="d-flex align-center">
      <AppTextField
        v-model.trim="newAlias"
        placeholder="john.doe@acme.com"
        type="email"
        :maxlength="255"
        style="max-width: 320px"
        :error-messages="emailErrors"
        @blur="validateEmail"
        @keypress.enter="addNewAlias"
      />
      <app-button
        class="ml-4 mb-auto"
        type="outlined"
        variant="neutral"
        :loading="addNewAliasPending"
        :disabled="aliasesPending"
        @click="addNewAlias"
      >
        {{ $t("common.add") }}
      </app-button>
    </div>

    <div class="mt-6">
      <div v-if="validateAliasPending || aliasesPending" class="text-center">
        <AppLoader small></AppLoader>
      </div>
      <template v-else>
        <div v-if="aliasesError" class="text-error">
          <app-alert-in-page
            variant="error"
            no-icon
            :dismissible="false"
          >
            {{ $t('profile.views.EmailAlias.getAliasesError') }}
          </app-alert-in-page>
        </div>
        <template v-if="aliases.length">
          <div
            v-for="(alias, index) in aliases"
            :key="index"
            class="section-container d-flex flex-column flex-md-row align-start align-md-center pa-4"
            :class="{
              'border-top-radius': index === 0,
              'border-bottom-radius': index === aliases.length - 1,
              'border-bottom-none': index !== aliases.length - 1,
            }"
          >
            <div class="d-flex flex-column">
              <p class="text--small text--bold">
                {{ alias.email }}
              </p>
              <template v-if="alias.verified">
                <div class="d-flex align-center column-gap-2">
                  <app-icon-badge
                    color="success"
                    class="mb-1 ml-1"
                    dot
                  >
                  </app-icon-badge>
                  <div class="text-success text--small text--bold">
                    {{ $t("profile.views.EmailAlias.verified") }}
                  </div>
                </div>
              </template>
              <div v-else class="d-flex flex-column flex-md-row align-start align-md-center column-gap-2">
                <div class="d-flex align-center column-gap-2 text-warning text--small text--bold">
                  <app-icon-badge
                    color="warning"
                    class="mb-1 ml-1"
                    dot
                  />
                  {{ $t("profile.views.EmailAlias.pendingVerification") }}
                </div>
                <span>—</span>
                <template v-if="resendAliasCheckLinkPending[alias.id]">
                  <app-material-icon name="spinner-third" size="xs" type="fa" spin />
                </template>
                <template v-else>
                  <app-button-ds
                    variant="text"
                    class="text-darkblue-500 text--bold"
                    @click="resendAliasCheckLink(alias.id)"
                  >
                    {{ $t('profile.views.EmailAlias.resendLink') }}
                  </app-button-ds>
                </template>
              </div>
            </div>
            <app-button
              size="small"
              type="outlined"
              variant="danger"
              class="ml-auto"
              @click="openDeleteAliasDialog(alias)"
            >
              {{ $t('common.delete') }}
            </app-button>
          </div>
        </template>
      </template>
    </div>

    <EmailAliasDeleteDialog
      v-if="deleteAliasDialog.isOpen"
      :alias="deleteAliasDialog.alias"
      @close="closeDeleteAliasDialog"
    />
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapActions, mapMutations, mapState } from 'vuex'

import AppLoader from '@/common/AppLoader.vue'
import AppTextField from '@/common/AppTextField.vue'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import {
  GET_ALIASES,
  ADD_NEW_ALIAS,
  VALIDATE_USER_ALIAS,
  RESEND_ALIAS_CHECK_LINK,
} from '@/store/modules/user/action_types'
import { SET_DELETE_ALIAS_DIALOG } from '@/store/modules/user/mutation_types'
import { postmarkEmail } from '@/validators/postmarkEmail'

import EmailAliasDeleteDialog from './EmailAliasDeleteDialog.vue'

export default {
  name: 'EmailAlias',
  components: { AppLoader, AppTextField, EmailAliasDeleteDialog },
  setup: () => ({ v$: useVuelidate() }),
  validations: {
    newAlias: { required, postmarkEmail },
  },
  computed: {
    ...mapState('user', [
      'profile',
      'aliasesPending',
      'aliases',
      'aliasesError',
      'addNewAliasPending',
      'validateAliasPending',
      'resendAliasCheckLinkPending',
      'deleteAliasDialog',
    ]),
    emailErrors() {
      return formFieldErrors(
        this.v$.newAlias,
        [
          ['postmarkEmail', this.$t('common.validations.email')],
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('profile.views.EmailAlias.addAlias') })],
        ],
      )
    },
  },
  data() {
    return {
      newAlias: '',
    }
  },
  created () {
    const token = this.$route.params.token
    if (token) {
      // If the token is present, validate the user email alias
      this.validateUserAlias(token)
    } else {
      this.getAlias()
    }
  },
  watch: {
    newAlias (newval) {
      // Do not display error when the email field is empty
      if (newval === '') {
        this.v$.newAlias.$reset()
      }
    },
  },
  methods: {
    ...mapActions('user', [
      GET_ALIASES,
      ADD_NEW_ALIAS,
      VALIDATE_USER_ALIAS,
      RESEND_ALIAS_CHECK_LINK,
    ]),
    ...mapMutations('user', [SET_DELETE_ALIAS_DIALOG]),
    async getAlias () {
      try {
        await this.GET_ALIASES()
      } catch (e) {
        toast.error(this.$t('profile.views.EmailAlias.getAliasesError'))
      }
    },
    async validateUserAlias (token) {
      try {
        const response = await this.VALIDATE_USER_ALIAS({ token })

        this.toast.success($t('profile.views.EmailAlias.validateAliasSuccess', { email: response?.data?.email }))
      } catch (e) {
        if (e?.response?.data?.errorCode === 'ERR_INVALID_TOKEN') {
          toast.error(this.$t('profile.views.EmailAlias.validateAliasExpired'))
        } else if (e?.response?.data?.errorCode === 'ERR_NOT_FOUND') {
          toast.error(this.$t('profile.views.EmailAlias.validateAliasAfterDeletion'))
        } else {
          toast.error(this.$t('profile.views.EmailAlias.validateAliasError'))
        }
      } finally {
        this.getAlias()
      }
    },
    validateEmail () {
      this.v$.newAlias.$touch()
      // Do not display error when leaving the empty email field
      if (this.newAlias === '') {
        this.v$.newAlias.$reset()
      }
    },
    async addNewAlias () {
      this.v$.newAlias.$touch()
      if (!this.v$.newAlias.$invalid) {
        try {
          await this.ADD_NEW_ALIAS({
            email: this.newAlias,
          })
          toast.success(this.$t('profile.views.EmailAlias.sendEmailSuccess', { email: this.newAlias }))
          this.newAlias = ''
        } catch (e) {
          if (e?.response?.data?.errorCode === 'ERR_EXISTING_EMAIL') {
            toast.error(this.$t('profile.views.EmailAlias.addAliasErrorExistingEmail'))
          } else {
            toast.error(this.$t('profile.views.EmailAlias.addAliasError'))
          }
        }
      }
    },
    async resendAliasCheckLink (aliasId) {
      try {
        await this.RESEND_ALIAS_CHECK_LINK({ aliasId })

        toast.success(this.$t('profile.views.EmailAlias.verificationLinkSentSuccess'))
      } catch (e) {
        console.error(e)
        toast.error(this.$t('profile.views.EmailAlias.verificationLinkSentError'))
      }
    },
    openDeleteAliasDialog (alias) {
      this.SET_DELETE_ALIAS_DIALOG({
        alias,
        isOpen: true,
      })
    },
    closeDeleteAliasDialog () {
      this.SET_DELETE_ALIAS_DIALOG({
        alias: null,
        isOpen: false,
      })
    },
  },
}
</script>

<style lang="scss">
  .user-email-alias {    
    .section-container {
      border: 1px solid #D4D7DA;
    }
    .border-top-radius {
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
    }
    .border-bottom-radius {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    }
    .border-bottom-none {
      border-bottom: none;
    }
  }
</style>
